import {
  CompetencyMapGenQuestionsLogQuery,
  CompetencyMapGenQuestionsLogQueryResult,
  CompetencyMapQuery,
  CompetencyMapQueryResult,
} from "@generated/graphql";
import { Box, Card, LinearProgress, Stack, Typography } from "@mui/material";
import QuestionsGeneratingLogs from "components/QuestionsGeneratingLogs";
import withHeightFitContent from "hocs/withHeightFitContent";
import { useMemo, useRef } from "react";
import { extractSkills } from "../../utils";
import Content from "./components/Content";
import { CompetencyMapWithProgressInfo } from "./types";

const ContentWithHeightFitContent = withHeightFitContent(Content);

interface ProgressInfoProps {
  title: string;
  competencyMapWithProgressInfo: CompetencyMapWithProgressInfo | undefined;
  competencyMapGenQuestionsLogsData?: CompetencyMapGenQuestionsLogQuery;
  refetchCompetencyMapGenQuestionsLog: CompetencyMapGenQuestionsLogQueryResult["refetch"];
  competencyMap: CompetencyMapQuery["competencyMap"];
  refetchCompetencyMap: CompetencyMapQueryResult["refetch"];
  heightFitContentSignal?: unknown;
}

const ProgressInfo = ({
  title,
  competencyMapWithProgressInfo,
  competencyMapGenQuestionsLogsData,
  refetchCompetencyMapGenQuestionsLog,
  competencyMap,
  refetchCompetencyMap,
  heightFitContentSignal,
}: ProgressInfoProps) => {
  const progressInfoRef = useRef<HTMLDivElement>(null);

  const progressPercent = competencyMapWithProgressInfo?.progressPercent ?? 0;
  const skills = extractSkills(competencyMap);
  const competencyMapGenQuestionsLogs = competencyMapGenQuestionsLogsData?.competencyMapGenQuestionsLog;

  const extendedLogs = useMemo(
    () =>
      competencyMapGenQuestionsLogs?.map((log) => ({
        ...log,
        skillTitle: log.skillId ? skills[log.skillId]?.title || "" : "",
      })),
    [competencyMapGenQuestionsLogs, skills]
  );

  const cardPaddingBottom = 20;

  return (
    <Card sx={{ pb: `${cardPaddingBottom}px` }}>
      <Stack spacing={2}>
        {!!extendedLogs?.length && (
          <Box>
            <QuestionsGeneratingLogs
              logs={extendedLogs}
              refetchLogs={refetchCompetencyMapGenQuestionsLog}
              afterRefetchLogs={refetchCompetencyMap}
            />
          </Box>
        )}
        <Typography
          variant='h20'
          sx={{ textAlign: "center", fontWeight: 500, whiteSpace: "pre-wrap" }}>
          {title}
        </Typography>
        <Stack spacing={1}>
          <Stack
            direction='row'
            justifyContent='space-between'>
            <Typography sx={{ fontWeight: 700 }}>Прогресс заполнения</Typography>
            <Typography sx={{ fontWeight: 700 }}>{progressPercent}%</Typography>
          </Stack>
          <LinearProgress
            variant='determinate'
            color={progressPercent === 100 ? "primary" : "warning"}
            value={progressPercent}
            sx={{ height: 10, borderRadius: 2 }}
          />
        </Stack>
        <ContentWithHeightFitContent
          competencyMapWithProgressInfo={competencyMapWithProgressInfo}
          refProp={progressInfoRef}
          withoutScrollTopButton
          additionalMaxHeightOffset={cardPaddingBottom}
          recalculateSignal={heightFitContentSignal}
        />
      </Stack>
    </Card>
  );
};

export default ProgressInfo;
